import { useEffect, useRef, useState } from "react";

interface FadeInProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: "up" | "down" | "left" | "right" | "none";
  initiallyVisible?: boolean;
}

const directionMap = {
  up: "translate(0, 32px)",
  down: "translate(0, -32px)",
  left: "translate(32px, 0)",
  right: "translate(-32px, 0)",
  none: "translate(0, 0)",
};

const FadeIn = ({ children, className = "", delay = 0, direction = "up", initiallyVisible = false }: FadeInProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "-60px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : (initiallyVisible ? 1 : 0),
        transform: visible ? "translate(0, 0)" : directionMap[direction],
        transition: `opacity 0.7s cubic-bezier(0.25, 0.4, 0.25, 1) ${delay}s, transform 0.7s cubic-bezier(0.25, 0.4, 0.25, 1) ${delay}s`,
        willChange: visible ? "auto" : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
};

export default FadeIn;
